/**
 * AWE Privacy Choices
 *
 * <awe-privacy-choices-component> powers the "Do Not Sell or Share My Personal
 * Information" switch on the Your Privacy Choices page.
 *
 * Opting out sets marketing = false and sale_of_data = false in Shopify's
 * Customer Privacy API. Shopify, its pixels, consent-aware apps and Pandectes
 * (the cookie banner app) all read that state. Opting back in restores both.
 * Pandectes' own JS API is enterprise-plan only, so it isn't used here.
 *
 * A Global Privacy Control signal (navigator.globalPrivacyControl) is treated
 * as an opt-out that the visitor can only change in their browser, so the
 * switch is locked on while it's present.
 */
(() => {
  const API_TIMEOUT_MS = 8000;
  const POLL_MS = 100;

  const gpcEnabled = () => navigator.globalPrivacyControl === true;

  const customerPrivacy = () => (window.Shopify && window.Shopify.customerPrivacy) || null;

  // Pandectes normally loads the Customer Privacy API; load it ourselves if nothing has.
  function loadCustomerPrivacy() {
    if (customerPrivacy() || !window.Shopify || typeof window.Shopify.loadFeatures !== "function") return;
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], () => {});
  }

  function waitForCustomerPrivacy() {
    return new Promise(resolve => {
      const start = Date.now();
      (function poll() {
        const cp = customerPrivacy();
        if (cp || Date.now() - start >= API_TIMEOUT_MS) return resolve(cp);
        setTimeout(poll, POLL_MS);
      })();
    });
  }

  function allowed(cp, method) {
    if (typeof cp[method] !== "function") return true;
    try {
      return cp[method]() !== false;
    } catch (_) {
      return true;
    }
  }

  class AwePrivacyChoicesComponent extends HTMLElement {
    connectedCallback() {
      this.switch = this.querySelector("[data-awe-privacy-switch]");
      this.switchState = this.querySelector("[data-awe-privacy-switch-state]");
      this.status = this.querySelector("[data-awe-privacy-status]");
      this.gpcNote = this.querySelector("[data-awe-privacy-gpc]");
      this.busy = false;

      if (!this.switch) return;

      this.onSwitchClick = this.onSwitchClick.bind(this);
      this.onConsentChange = this.render.bind(this);

      this.switch.addEventListener("click", this.onSwitchClick);
      // Fired by Shopify when consent changes, including from the cookie banner.
      document.addEventListener("visitorConsentCollected", this.onConsentChange);

      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener("visitorConsentCollected", this.onConsentChange);
    }

    async init() {
      loadCustomerPrivacy();
      if (!(await waitForCustomerPrivacy())) {
        this.setStatus(this.dataset.statusError);
        return;
      }
      this.render();
    }

    /** True when sale/sharing is currently opted out on this browser. */
    isOptedOut() {
      if (gpcEnabled()) return true;
      const cp = customerPrivacy();
      if (!cp) return false;
      return !allowed(cp, "saleOfDataAllowed") || !allowed(cp, "marketingAllowed");
    }

    render() {
      if (this.busy) return;
      const optedOut = this.isOptedOut();
      const gpc = gpcEnabled();

      this.switch.setAttribute("aria-checked", String(optedOut));
      this.switch.disabled = gpc || !customerPrivacy();
      if (this.switchState) {
        this.switchState.textContent = optedOut ? this.dataset.switchOn : this.dataset.switchOff;
      }
      if (this.gpcNote) this.gpcNote.hidden = !gpc;

      this.setStatus(optedOut ? this.dataset.statusOptedOut : this.dataset.statusOptedIn);
    }

    setStatus(message) {
      if (this.status && message != null) this.status.textContent = message;
    }

    async onSwitchClick() {
      if (this.busy || gpcEnabled()) return;
      const optOut = this.switch.getAttribute("aria-checked") !== "true";

      this.busy = true;
      this.switch.setAttribute("aria-busy", "true");

      try {
        await this.saveConsent(optOut);
        this.busy = false;
        this.render();
        this.setStatus(optOut ? this.dataset.statusSavedOut : this.dataset.statusSavedIn);
      } catch (_) {
        this.busy = false;
        this.render();
        this.setStatus(this.dataset.statusError);
      } finally {
        this.switch.removeAttribute("aria-busy");
      }
    }

    saveConsent(optOut) {
      const cp = customerPrivacy();
      if (!cp || typeof cp.setTrackingConsent !== "function") return Promise.reject(new Error("unavailable"));

      return new Promise((resolve, reject) => {
        cp.setTrackingConsent({ marketing: !optOut, sale_of_data: !optOut }, result => {
          if (result && result.error) reject(new Error(result.error));
          else resolve();
        });
      });
    }
  }

  if (!customElements.get("awe-privacy-choices-component")) {
    customElements.define("awe-privacy-choices-component", AwePrivacyChoicesComponent);
  }
})();
